import { useState } from "react";

/**
 * Unified collapsible accordion for integration blocks.
 *
 * Visual contract matches the existing CMS-integration accordions in the
 * Dashboard ("Integrations" tab) — border-2 border-black, large clickable
 * header with logo, title, description and rotating chevron.
 *
 * Adds an optional status badge (АКТИВНО / ОТКЛЮЧЕНО) for integrations that
 * have an on/off state (IDENT, VK, Telegram, WhatsApp). Pass `enabled={null}`
 * (or omit) to hide the badge for docs-only blocks.
 *
 * Always starts collapsed.
 */
export const CollapsibleIntegration = ({
  logo,
  title,
  description,
  enabled = null,
  enabledLabel = "АКТИВНО",
  disabledLabel = "ОТКЛЮЧЕНО",
  defaultOpen = false,
  children,
  testId,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className="border-2 border-black rounded-lg overflow-hidden"
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors"
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
          {logo && (
            <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 flex items-center justify-center">
              {logo}
            </div>
          )}
          <div className="text-left flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg sm:text-xl font-bold">{title}</h3>
              {enabled !== null && (
                <span
                  className={`text-[10px] sm:text-xs px-2 py-0.5 rounded font-bold text-white whitespace-nowrap ${
                    enabled ? "bg-green-600" : "bg-gray-400"
                  }`}
                  data-testid={testId ? `${testId}-status` : undefined}
                >
                  {enabled ? enabledLabel : disabledLabel}
                </span>
              )}
            </div>
            {description && (
              <p className="text-gray-600 text-xs sm:text-sm mt-1 line-clamp-2">
                {description}
              </p>
            )}
          </div>
        </div>
        <svg
          className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform flex-shrink-0 ml-2 ${
            open ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div className="border-t-2 border-black p-4 sm:p-6 bg-gray-50">
          {children}
        </div>
      )}
    </div>
  );
};

export default CollapsibleIntegration;
